/**
 * Campaign Team Modal — DOM modal for viewing and editing the team roster.
 */
import type { CampaignState, CampaignUnit } from '../types';
import { BOT_PRESETS, BOT_SKILL_LEVELS, MAX_CAMPAIGN_UNITS, UPGRADE_AP_COST, UPGRADE_SKILL_COST, UPGRADE_FITNESS_COST, UPGRADE_JUMP_COST, SKILL_PROGRESSION, MAX_UPGRADE_LEVEL, MAX_AP_LEVEL } from '../constants';
import { cur } from './flavor';
import { theme } from './theme';
import { thumbnails, thumbCanvasHtml } from '../rendering/modelThumbnails';

/** Campaign roster fights as team 0 (the first company palette). */
const CAMPAIGN_TEAM_INDEX = 0;

// =============================================================================
// MODULE STATE
// =============================================================================

let modalEl: HTMLDivElement | null = null;
let slotsEl: HTMLDivElement | null = null;
let editEl: HTMLDivElement | null = null;
let selectedUnitId = 0;

let currentState: CampaignState | null = null;
let currentCallbacks: {
  onRename: (unitId: number, newName: string) => void;
  onUpgradeAp?: (unitId: number) => void;
  onUpgradeSkill?: (unitId: number) => void;
  onUpgradeFitness?: (unitId: number) => void;
  onUpgradeJump?: (unitId: number) => void;
  onClose: () => void;
} | null = null;

// =============================================================================
// SHOW / HIDE / UPDATE
// =============================================================================

export function showCampaignTeam(
  state: CampaignState,
  callbacks: {
    onRename: (unitId: number, newName: string) => void;
    onUpgradeAp?: (unitId: number) => void;
    onUpgradeSkill?: (unitId: number) => void;
    onUpgradeFitness?: (unitId: number) => void;
    onUpgradeJump?: (unitId: number) => void;
    onClose: () => void;
  }
): void {
  currentState = state;
  currentCallbacks = callbacks;
  selectedUnitId = 0;

  if (!modalEl) {
    createModal();
  }

  modalEl!.style.display = 'flex';
  renderContent();
}

export function updateCampaignTeam(state: CampaignState): void {
  currentState = state;
  if (!modalEl || modalEl.style.display === 'none') return;
  renderContent();
}

export function hideCampaignTeam(): void {
  thumbnails.clear();
  if (modalEl) {
    modalEl.style.display = 'none';
  }
}

// =============================================================================
// DOM CREATION
// =============================================================================

function createModal(): void {
  modalEl = document.createElement('div');
  modalEl.id = 'campaign-team-modal';
  modalEl.style.cssText = `
    position:fixed; inset:0; z-index:105;
    display:flex; align-items:center; justify-content:center;
    background:${theme.surface.overlay};
    font-family:'Space Grotesk',monospace;
  `;
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) currentCallbacks?.onClose();
  });

  const panel = document.createElement('div');
  panel.style.cssText = `
    width:600px; max-height:70vh; background:${theme.surface.panelBg};
    border:1px solid ${theme.surface.border}; border-radius:10px;
    display:flex; flex-direction:column; overflow:hidden;
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    padding:14px 18px; display:flex; align-items:center; justify-content:space-between;
    border-bottom:1px solid ${theme.goldBorder20};
  `;
  header.innerHTML = `
    <span style="font-size:15px; color:${theme.accent}; font-weight:700; letter-spacing:2px;">TEAM ROSTER</span>
    <button id="campaign-team-close" style="
      background:none; border:none; color:${theme.text.secondary}; font-size:18px; cursor:pointer;
      padding:4px 8px; line-height:1;
    ">&times;</button>
  `;
  panel.appendChild(header);

  // Body: flex row
  const body = document.createElement('div');
  body.style.cssText = `
    flex:1; display:flex; overflow:hidden; min-height:200px;
  `;

  // Left column: unit slots
  slotsEl = document.createElement('div');
  slotsEl.style.cssText = `
    width:200px; flex-shrink:0; overflow-y:auto; padding:12px;
    border-right:1px solid ${theme.goldBorder15};
    display:flex; flex-direction:column; gap:6px;
  `;
  body.appendChild(slotsEl);

  // Right column: edit panel
  editEl = document.createElement('div');
  editEl.style.cssText = `
    flex:1; padding:16px; overflow-y:auto;
  `;
  body.appendChild(editEl);

  panel.appendChild(body);
  modalEl.appendChild(panel);
  document.body.appendChild(modalEl);

  document.getElementById('campaign-team-close')?.addEventListener('click', () => {
    currentCallbacks?.onClose();
  });
}

// =============================================================================
// RENDERING
// =============================================================================

function renderContent(): void {
  if (!slotsEl || !editEl || !currentState) return;
  renderSlots();
  renderEditPanel();
}

function renderSlots(): void {
  if (!slotsEl || !currentState) return;
  slotsEl.innerHTML = '';

  for (let i = 0; i < MAX_CAMPAIGN_UNITS; i++) {
    const unit = currentState.roster.find(u => u.id === i);
    const slot = document.createElement('div');
    const isSelected = unit ? unit.id === selectedUnitId : false;

    if (unit) {
      const isDead = !unit.alive;
      slot.style.cssText = `
        padding:8px 10px; border-radius:6px; cursor:pointer;
        border:1px solid ${isSelected ? theme.accent : theme.surface.borderSubtle};
        background:${isSelected ? theme.goldBg12 : theme.rowBgSelected};
        ${isDead ? 'opacity:0.4;' : ''}
        transition: border-color 0.15s;
      `;

      const tagHtml = unit.id === 0
        ? `<span style="font-size:9px; color:${theme.accent}; background:${theme.goldBorder15}; padding:1px 5px; border-radius:3px; letter-spacing:1px; margin-left:4px;">CMD</span>`
        : '';

      const personalityChip = `<span style="font-size:9px; color:${theme.chip.text}; background:${theme.chipBg}; padding:1px 5px; border-radius:3px;">${unit.personalityKey}</span>`;
      const skillBadge = `<span style="font-size:9px; color:${theme.text.secondary};">${unit.skillKey}</span>`;
      const apColor = unit.ap > 0 ? theme.positive : theme.negative;
      const apBadge = `<span style="font-size:9px; color:${apColor};">AP ${unit.ap}/${unit.maxAp}</span>`;

      slot.innerHTML = `
        <div style="display:flex; align-items:center; gap:8px;">
          ${isDead ? '' : thumbCanvasHtml(`player:${CAMPAIGN_TEAM_INDEX}`, 44, `background:${theme.chipBg}; border-radius:6px; flex-shrink:0;`)}
          <div style="flex:1; min-width:0;">
            <div style="display:flex; align-items:center; gap:4px;">
              <span style="font-size:12px; color:${isDead ? theme.negative : theme.text.primary}; font-weight:600; ${isDead ? 'text-decoration:line-through;' : ''}">${unit.name}</span>
              ${tagHtml}
            </div>
            <div style="display:flex; gap:4px; margin-top:4px; align-items:center; flex-wrap:wrap;">
              ${personalityChip} ${skillBadge} ${apBadge}
            </div>
          </div>
        </div>
      `;

      slot.addEventListener('click', () => {
        selectedUnitId = unit.id;
        renderContent();
      });
    } else {
      slot.style.cssText = `
        padding:8px 10px; border-radius:6px;
        border:1px dashed ${theme.emptySlotBorder};
        background:transparent; opacity:0.4;
      `;
      slot.innerHTML = `<span style="font-size:11px; color:${theme.text.muted}; letter-spacing:1px;">EMPTY</span>`;
    }

    slotsEl.appendChild(slot);
  }
  thumbnails.mount(slotsEl);
}

function renderEditPanel(): void {
  if (!editEl || !currentState) return;

  const unit = currentState.roster.find(u => u.id === selectedUnitId);
  if (!unit) {
    editEl.innerHTML = `<div style="color:${theme.text.secondary}; font-size:12px; padding:20px; text-align:center;">
      Select a unit to view details.
    </div>`;
    return;
  }

  const personalityDef = BOT_PRESETS[unit.personalityKey];
  const skillValue = BOT_SKILL_LEVELS[unit.skillKey];

  editEl.innerHTML = `
    <div style="display:flex; justify-content:center; margin-bottom:14px;">
      ${thumbCanvasHtml(`player:${CAMPAIGN_TEAM_INDEX}`, 180, `background:${theme.chipBg}; border:1px solid ${theme.surface.borderSubtle}; border-radius:10px;`)}
    </div>
    <div style="margin-bottom:16px;">
      <label style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; display:block; margin-bottom:6px;">NAME</label>
      <input id="campaign-team-name-input" type="text" maxlength="20" value="${escapeHtml(unit.name)}" style="
        width:100%; box-sizing:border-box; padding:8px 10px;
        background:${theme.chipBg}; border:1px solid ${theme.surface.border};
        border-radius:4px; color:${theme.text.primary}; font-family:'Space Grotesk',monospace; font-size:13px;
        outline:none;
      " />
    </div>

    <div style="margin-bottom:16px;">
      <label style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; display:block; margin-bottom:6px;">PERSONALITY</label>
      <div style="
        padding:8px 10px; background:${theme.rowBg}; border:1px solid ${theme.surface.borderSubtle};
        border-radius:4px; font-size:12px; color:${theme.text.primary};
      ">
        <div style="font-weight:600; margin-bottom:4px;">${unit.personalityKey.charAt(0).toUpperCase() + unit.personalityKey.slice(1)}</div>
        ${personalityDef ? `<div style="font-size:10px; color:${theme.text.secondary};">
          Aggression ${(personalityDef.aggression * 100).toFixed(0)}% &middot;
          Mobility ${(personalityDef.mobility * 100).toFixed(0)}% &middot;
          Risk ${(personalityDef.riskTolerance * 100).toFixed(0)}%
        </div>` : ''}
      </div>
    </div>

    <div style="margin-bottom:16px;">
      <label style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; display:block; margin-bottom:6px;">SKILL</label>
      <div style="
        padding:8px 10px; background:${theme.rowBg}; border:1px solid ${theme.surface.borderSubtle};
        border-radius:4px; font-size:12px; color:${theme.text.primary};
      ">
        <span style="font-weight:600;">${unit.skillKey.charAt(0).toUpperCase() + unit.skillKey.slice(1)}</span>
        <span style="color:${theme.text.secondary}; margin-left:8px;">(${skillValue !== undefined ? (skillValue * 100).toFixed(0) + '%' : '?'})</span>
        ${''}
      </div>
    </div>

    <div style="margin-bottom:16px;">
      <label style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; display:block; margin-bottom:6px;">ACTION POINTS</label>
      <div style="
        padding:8px 10px; background:${theme.rowBg}; border:1px solid ${theme.surface.borderSubtle};
        border-radius:4px; font-size:12px; color:${unit.ap > 0 ? theme.positive : theme.negative};
      ">
        <span style="font-weight:600;">${unit.ap} / ${unit.maxAp}</span>
        ${unit.ap <= 0 ? `<span style="color:${theme.text.secondary}; margin-left:8px;">Exhausted — resets next wave</span>` : `<span style="color:${theme.text.secondary}; margin-left:8px;">Available for missions</span>`}
      </div>
    </div>

    <div style="margin-bottom:16px;">
      <label style="font-size:10px; color:${theme.text.secondary}; letter-spacing:1px; display:block; margin-bottom:6px;">TRAINING &amp; TRAITS</label>
      <div style="display:flex; flex-direction:column; gap:6px;">
        <div style="display:flex; gap:8px;">
          ${renderUpgradeBtn('campaign-team-upgrade-ap', `+1 AP`, UPGRADE_AP_COST, unit.maxAp < MAX_AP_LEVEL, currentState!.playerMoney, `${unit.maxAp}/${MAX_AP_LEVEL}`)}
          ${renderUpgradeBtn('campaign-team-upgrade-skill', 'SKILL UP', UPGRADE_SKILL_COST, SKILL_PROGRESSION.indexOf(unit.skillKey) < SKILL_PROGRESSION.length - 1, currentState!.playerMoney, `${unit.skillKey}`)}
        </div>
        <div style="display:flex; gap:8px;">
          ${renderUpgradeBtn('campaign-team-upgrade-fitness', 'FITNESS', UPGRADE_FITNESS_COST, (unit.fitnessLevel ?? 0) < MAX_UPGRADE_LEVEL, currentState!.playerMoney, `${unit.fitnessLevel ?? 0}/${MAX_UPGRADE_LEVEL}`)}
          ${renderUpgradeBtn('campaign-team-upgrade-jump', 'JUMP', UPGRADE_JUMP_COST, (unit.jumpLevel ?? 0) < MAX_UPGRADE_LEVEL, currentState!.playerMoney, `${unit.jumpLevel ?? 0}/${MAX_UPGRADE_LEVEL}`)}
        </div>
      </div>
    </div>

    ${!unit.alive ? `<div style="color:${theme.negative}; font-size:11px; font-weight:600; letter-spacing:1px; margin-top:12px;">KIA — PERMADEATH</div>` : ''}
  `;
  thumbnails.mount(editEl);

  // Wire name input
  const nameInput = document.getElementById('campaign-team-name-input') as HTMLInputElement | null;
  if (nameInput) {
    const commitName = () => {
      const newName = nameInput.value;
      if (newName.trim() && newName.trim() !== unit.name) {
        currentCallbacks?.onRename(unit.id, newName);
      }
    };
    nameInput.addEventListener('blur', commitName);
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        nameInput.blur();
      }
    });
  }

  // Wire upgrade buttons
  document.getElementById('campaign-team-upgrade-ap')?.addEventListener('click', () => {
    currentCallbacks?.onUpgradeAp?.(unit.id);
  });
  document.getElementById('campaign-team-upgrade-skill')?.addEventListener('click', () => {
    currentCallbacks?.onUpgradeSkill?.(unit.id);
  });
  document.getElementById('campaign-team-upgrade-fitness')?.addEventListener('click', () => {
    currentCallbacks?.onUpgradeFitness?.(unit.id);
  });
  document.getElementById('campaign-team-upgrade-jump')?.addEventListener('click', () => {
    currentCallbacks?.onUpgradeJump?.(unit.id);
  });
}

function renderUpgradeBtn(id: string, label: string, cost: number, canUpgrade: boolean, money: number, levelText: string): string {
  const affordable = money >= cost;
  const enabled = canUpgrade && affordable;
  const maxed = !canUpgrade;
  return `<button id="${id}" ${!enabled ? 'disabled' : ''} style="
    flex:1; padding:6px 8px; border-radius:4px; cursor:${enabled ? 'pointer' : 'default'}; font-family:'Space Grotesk',monospace;
    font-size:10px; font-weight:600; letter-spacing:0.5px;
    background:${enabled ? theme.goldBg12 : theme.rowBg};
    border:1px solid ${enabled ? theme.surface.border : theme.surface.borderSubtle};
    color:${enabled ? theme.accent : theme.text.disabled};
  ">${label} <span style="font-size:9px; opacity:0.7;">[${levelText}]</span><br><span style="font-size:9px;">${maxed ? 'MAX' : cost + ' ' + cur()}</span></button>`;
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
